import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel, { InputLabelProps } from '@mui/material/InputLabel';
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem';
import MuiSelect, { SelectProps as MuiSelectProps } from '@mui/material/Select';
import React from 'react';
import LangEnum from '../../../lang/LangEnum';
import Typography from '../../Typography/Typography';
import { useSelectStyles } from './Select.styles';

export type SelectProps = MuiSelectProps & {
  disabled?: boolean;
  helperText?: string;
  error?: boolean;
  fullWidth?: boolean;
  label?: string;
  showEmpty?: boolean;
  emptyLabel?: string;
  items?: Array<{
    value?: string | number | string[];
    label: string | LangEnum;
    description?: string | LangEnum;
  }>;
  inputLabelProps?: InputLabelProps;
  formControlProps?: FormControlProps;
  menuItemProps?: MenuItemProps;
  selectDisplayTestId?: string;
};

const Select = React.forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      children,
      disabled,
      helperText,
      error,
      fullWidth,
      label,
      items = [],
      formControlProps = {},
      variant,
      onBlur,
      inputLabelProps = {},
      showEmpty,
      emptyLabel = 'selecione',
      menuItemProps,
      selectDisplayTestId,
      ...props
    },
    ref,
  ) => {
    const { classes: selectStyles } = useSelectStyles();
    return (
      <FormControl
        {...formControlProps}
        error={error}
        fullWidth={fullWidth}
        variant={variant}
        disabled={disabled}
      >
        <InputLabel
          size={props?.size === 'small' || !props?.size ? 'small' : 'medium'}
          {...inputLabelProps}
        >
          {label}
        </InputLabel>
        <MuiSelect
          fullWidth={fullWidth}
          variant={variant}
          label={label}
          onBlur={onBlur}
          disabled={disabled}
          ref={ref}
          IconComponent={ExpandMoreRounded}
          SelectDisplayProps={
            selectDisplayTestId
              ? ({ 'test-id': selectDisplayTestId } as React.HTMLAttributes<HTMLDivElement>)
              : undefined
          }
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
          }}
          renderValue={(selected) => {
            if (Array.isArray(selected)) {
              const selectedItems = items?.filter((item) => selected.includes(item.value));
              return selectedItems.map((item) => item.label).join(', ');
            }

            const selectedItem = items?.find((item) => item.value === selected);
            return selectedItem ? selectedItem.label : '';
          }}
          {...props}
        >
          {showEmpty && (
            <MenuItem value="" {...menuItemProps}>
              {emptyLabel}
            </MenuItem>
          )}
          {children ||
            items?.map((item) => (
              <MenuItem key={`${item.label}-${item.value}`} value={item.value} {...menuItemProps}>
                <div className={selectStyles.itemContainer}>
                  <Typography variant="body1-regular" color="textPrimary">
                    {item.label}
                  </Typography>
                  {item.description && (
                    <Typography variant="body2-light">{item.description}</Typography>
                  )}
                </div>
              </MenuItem>
            ))}
        </MuiSelect>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  },
);

export default Select;
